import useBatteryProposalArbitrage from "~/api/hooks/useBatteryProposalArbitrage"

import { batteryArbitrageReasonCopy } from "./battery-arbitrage-status"

export function BatterySavingsSupport({
  proposalId,
  hasUnsavedInputs,
}: {
  proposalId: string
  hasUnsavedInputs: boolean
}): JSX.Element {
  const query = useBatteryProposalArbitrage(proposalId)
  const support = query.data?.support
  const view = hasUnsavedInputs
    ? {
        heading: `Confirm inputs to check estimate support`,
        detail: `Estimate support will update after the rate plan and usage are saved.`,
      }
    : query.isError
      ? {
          heading: `Estimate support could not be checked`,
          detail: `You can continue setup and check again in Battery Savings.`,
        }
      : query.isFetching || !support || support.estimate === `undetermined`
        ? {
            heading: `Checking estimate support`,
            detail: `An estimate has not been established for the current inputs. Support updates when the calculation finishes.`,
          }
        : support.estimate === `unavailable`
          ? {
              heading: `Estimate unavailable`,
              detail: batteryArbitrageReasonCopy(support.reason).detail,
            }
          : {
              heading:
                support.estimate === `personalized`
                  ? `Personalized estimate available`
                  : `Generalized estimate available`,
              detail:
                support.estimate === `personalized`
                  ? `The current calculation uses this home's measured usage and supported billing inputs.`
                  : `The current calculation is a modeled scenario. Its usage, rate assumptions, and billing scope are disclosed with the estimate.`,
            }

  return (
    <section
      role="status"
      aria-live="polite"
      aria-atomic="true"
      aria-label="Savings estimate support"
      className="rounded-lg border border-border bg-background p-4"
    >
      <h3 className="text-sm font-semibold text-foreground">{view.heading}</h3>
      <p className="mt-1 text-sm text-muted">{view.detail}</p>
      <p className="mt-2 text-xs text-muted">
        Support does not guarantee positive savings. You can keep your Utility
        Company and rate plan and continue setup regardless of estimate support.
      </p>
    </section>
  )
}
